/**
 * Skida PRO companies and the guides inside them.
 *
 * Both collections are small — 86 companies, 121 memberships — so unlike the
 * consumer users table this one loads whole. That is a deliberate difference:
 * paging exists to keep 49,000 personal records out of the tab, and it buys
 * nothing against a hundred rows.
 *
 * ## The joins
 *
 * A membership carries `uid`, which is the Firebase uid and therefore the user
 * document's own id, the `owner` on a drawn route, and the `appUserId` on a
 * subscription. Everything hangs off that one key.
 *
 * ## What cannot be answered here
 *
 * **Last sign-in is not in Firestore.** It lives on the Firebase Auth record as
 * `lastSignInTime` and only the Admin SDK can read it, so no amount of client
 * work will produce it. `scripts/syncProMembers.ts` mirrors it into
 * `skidaCrmProMembers` for the guides in these companies; where that has not
 * run, the column reads "not synced" rather than a date, so an empty cell is
 * never mistaken for "never signed in".
 */
import {
  collection,
  getCountFromServer,
  getDocs,
  query,
  where,
  type DocumentData,
} from 'firebase/firestore'
import { db, productDb } from '@/shared/firebase/client'
import { CRM_COLLECTIONS, PRODUCT_COLLECTIONS } from '@/shared/firebase/collections'
import { toIsoOrNull } from '@/shared/util/dates'

/**
 * What the customer typed into the billing step.
 *
 * Kept as its own record rather than flattened onto the company, because every
 * field here is theirs — a legal name that differs from the trading name, an
 * address, a VAT number and what the check made of it. It is the fullest
 * account of who a company actually is that exists anywhere in the product.
 */
export interface ProBillingProfile {
  /** The legal name, which is often not the name the workspace goes by. */
  companyName: string
  vatNumber: string
  /**
   * What the VAT check concluded: `valid`, `invalid`, `not_applicable` outside
   * the EU, or blank where it never ran.
   */
  vatStatus: string
  /** The name VIES holds for that number, when the check found one. */
  vatName: string
  /** Street lines, blanks dropped — a second line is usually empty. */
  addressLines: string[]
  postalCode: string
  city: string
  countryCode: string
  countryName: string
  /** The product's own sentence about how this company is taxed. */
  taxLabel: string
  taxType: string
  reverseCharge: boolean
  updatedAt: string | null
}

export interface ProCompany {
  id: string
  name: string
  tagline: string
  logoUrl: string
  email: string
  claimCode: string
  createdAt: string | null
  /** Last write to the company record itself, from any screen in the product. */
  updatedAt: string | null
  createdBy: string
  /**
   * The creator, resolved through the user documents.
   *
   * `createdBy` is a uid, and a uid is not somebody you can call. It is usually
   * the company's admin, but not always — a creator can leave the company and
   * stay on the record, which is worth seeing rather than hiding.
   */
  createdByName: string
  createdByEmail: string
  /**
   * The billing profile, whole, or null when the company never filled one in.
   *
   * Only 21 of 87 companies have one, and that is the point of reading it: the
   * profile is the last thing a company fills in before paying, so its contents
   * say how far a serious customer got and its absence says they never started.
   */
  billing: ProBillingProfile | null
  members: ProMember[]

  /**
   * ---- Routes and tours, counted on the company ----
   *
   * The company is the owner. Every one of the 546 routes and 61 tours carries
   * a `companyId`; a `guideId` is optional and often absent — 331 routes, three
   * fifths of them, have none at all.
   *
   * So these are the real figures for what a company has built. The per-guide
   * numbers below are a subset of them, not a decomposition: they will not add
   * up to these, and they are not meant to.
   */
  routes: number | null
  tours: number | null
  /** Routes and tours carrying no guide — company work nobody is credited for. */
  unattributedRoutes: number | null
  unattributedTours: number | null
  /** The latest route or tour made or edited — part of "activity" for the state. */
  lastWorkAt: string | null
}

export interface ProMember {
  uid: string
  companyId: string
  companyName: string
  role: 'admin' | 'member'
  /** When they joined this company — not when they signed up for Skida. */
  joinedAt: string | null
  inviteCode: string

  /** From the consumer user document. */
  name: string
  email: string
  /** Account creation. Around a third of accounts predate the field. */
  createdAt: string | null

  /**
   * Routes and tours **attributed to this guide** — not their share of the
   * company's work.
   *
   * Ownership sits on the company; `guideId` is optional and mostly missing on
   * routes. A guide showing 2 routes inside a company that has 40 has not made
   * two of forty: they are the only two that happened to be tagged.
   *
   * The two collections key a guide differently, which the queries respect:
   * `skidaProRoutes.guideId` is the membership key `companyId_uid`, while
   * `skidaProTours.guideId` is the bare uid.
   */
  routes: number | null
  tours: number | null

  /**
   * ---- Skida PRO access, read from the user document ----
   *
   * **PRO access does not come through RevenueCat.** It is written onto the
   * user: `subscription` for a paid company agreement billed through ePay, and
   * `skidaProAccess` for a granted one — a redeemed PRO promo code carrying its
   * own expiry.
   *
   * This has been wrong twice, in the same direction both times — overstating
   * who is paying.
   *
   * First it read the RevenueCat mirror and reported 49 guides as paying; those
   * were personal *consumer* Premium subscriptions, unrelated to PRO. Then it
   * treated the mere presence of a `subscription` object as payment, and
   * reported three — but the object is written when the ePay payment window
   * *opens*, not when it is paid. Two of those three sat at `state: "PENDING"`
   * with a null `subscriptionId`, meaning the checkout was abandoned. One
   * genuinely pays.
   *
   * So the state is read, not the existence of the field.
   *
   *   registered  in a company, no PRO access of any kind
   *   granted     a live PRO promo grant
   *   expired     a grant that has run out
   *   paid        an ePay agreement in state SUCCESS, still inside its period
   */
  access: 'registered' | 'granted' | 'expired' | 'paid'
  /** When a grant runs out, or the paid period ends. Null when neither applies. */
  accessEndsAt: string | null
  /**
   * An ePay checkout that did not result in access, kept separate from `access`
   * because it is not access — it is a sales signal.
   *
   * `pending` is somebody who opened the payment window and walked away, which
   * is the warmest lead this dashboard holds. `cancelled` and `lapsed` are a
   * customer who left. All three used to read as "Paid".
   */
  checkout: 'pending' | 'cancelled' | 'lapsed' | null
  /** The agreement's value, in whole currency units, for the checkout note. */
  checkoutAmount: string
  /** The promo code behind a grant, so a rep can see which campaign it was. */
  accessCode: string
  /**
   * Consumer Skida Premium, kept deliberately separate.
   *
   * A guide can hold a personal Premium subscription in the consumer app and no
   * PRO access at all — the two are unrelated products. Showing them in one
   * column is what produced the wrong number before.
   */
  consumerPremium: boolean

  /** Mirrored from Firebase Auth by a script; null means the sync has not run. */
  lastSignInAt: string | null

  /**
   * ---- Skida PRO onboarding, from the user document's `onboarding` map ----
   *
   * `currentStep` is the useful one. A guide who never finished is stopped
   * somewhere specific, and the step names where — which is a far better
   * prompt for a conversation than a bare "not completed".
   *
   * Null throughout means the field is absent, which is not the same as
   * unfinished: two of the guides in these companies predate it.
   */
  onboardingCompleted: boolean | null
  onboardingStep: string
  onboardedAt: string | null
  /**
   * When the wizard last moved.
   *
   * The step says where somebody stopped; this says when — and a guide stuck on
   * the paywall since March is a different conversation from one who stopped
   * yesterday.
   */
  onboardingUpdatedAt: string | null

  /**
   * How much of the guide profile they filled in — the part of onboarding that
   * carries into the product. Counted rather than listed, because the point is
   * whether the profile is worth showing a client, not which field is blank.
   */
  profileFilled: number
  profileTotal: number
}

const str = (v: unknown) => (typeof v === 'string' ? v : '')

/**
 * The billing profile as the customer left it, or null when there is none.
 *
 * Every field is read defensively: the profile is written by a form in the
 * product that has changed shape before, and a missing sub-map must render an
 * empty row rather than throw the whole dashboard.
 */
function billingProfile(raw: unknown): ProBillingProfile | null {
  if (!raw || typeof raw !== 'object') return null
  const v = raw as DocumentData
  const address = (v.billingAddress ?? {}) as DocumentData
  const tax = (v.taxTreatment ?? {}) as DocumentData
  const check = (v.vatValidation ?? {}) as DocumentData
  return {
    companyName: str(v.companyName),
    vatNumber: str(v.vatNumber) || str(check.normalizedVatNumber),
    vatStatus: str(check.status),
    vatName: str(check.viesName),
    addressLines: [str(address.line1), str(address.line2)].filter(Boolean),
    postalCode: str(address.postalCode),
    city: str(address.city),
    countryCode: str(address.countryCode) || str(tax.countryCode),
    countryName: str(address.countryName),
    taxLabel: str(tax.displayLabel),
    taxType: str(tax.type),
    reverseCharge: tax.reverseCharge === true,
    updatedAt: toIsoOrNull(v.updatedAt),
  }
}

/** Companies with their members attached, newest company first. */
export async function loadProCompanies(): Promise<ProCompany[]> {
  const [companySnap, memberSnap] = await Promise.all([
    getDocs(collection(productDb, PRODUCT_COLLECTIONS.companies)),
    getDocs(collection(productDb, PRODUCT_COLLECTIONS.companyMembers)),
  ])

  const memberships = memberSnap.docs.map((d) => {
    const v = d.data()
    return {
      uid: str(v.uid),
      companyId: str(v.companyId),
      role: str(v.role) === 'admin' ? ('admin' as const) : ('member' as const),
      joinedAt: toIsoOrNull(v.createdAt),
      inviteCode: str(v.inviteCode),
    }
  })

  const uids = [...new Set(memberships.map((m) => m.uid).filter(Boolean))]
  // The creators too, so `createdBy` can be shown as a person. Most are already
  // members; the ones that are not are exactly the interesting case — somebody
  // who set a company up and then left it.
  const creators = companySnap.docs.map((d) => str(d.get('createdBy'))).filter(Boolean)
  const [users, signIns] = await Promise.all([
    loadUsers([...new Set([...uids, ...creators])]),
    loadSignIns(uids),
  ])

  const companies: ProCompany[] = companySnap.docs.map((d) => {
    const v = d.data()
    const billing = billingProfile(v.billingProfile)
    const creator = users.get(str(v.createdBy))
    return {
      id: d.id,
      name: str(v.name) || billing?.companyName || '(unnamed company)',
      tagline: str(v.tagline),
      logoUrl: str(v.logoUrl),
      email: str(v.email),
      claimCode: str(v.claimCode),
      createdAt: toIsoOrNull(v.createdAt),
      updatedAt: toIsoOrNull(v.updatedAt),
      createdBy: str(v.createdBy),
      createdByName: creator?.name ?? '',
      createdByEmail: creator?.email ?? '',
      billing,
      members: [],
      routes: null,
      tours: null,
      unattributedRoutes: null,
      unattributedTours: null,
      lastWorkAt: null,
    }
  })
  const byId = new Map(companies.map((c) => [c.id, c]))

  for (const m of memberships) {
    const company = byId.get(m.companyId)
    if (!company) continue
    const u = users.get(m.uid)
    company.members.push({
      ...m,
      companyName: company.name,
      name: u?.name ?? '',
      email: u?.email ?? '',
      createdAt: u?.createdAt ?? null,
      routes: null,
      tours: null,
      onboardingCompleted: u?.onboardingCompleted ?? null,
      onboardingStep: u?.onboardingStep ?? '',
      onboardedAt: u?.onboardedAt ?? null,
      onboardingUpdatedAt: u?.onboardingUpdatedAt ?? null,
      profileFilled: u?.profileFilled ?? 0,
      profileTotal: PROFILE_FIELDS.length,
      access: u?.access ?? 'registered',
      checkout: u?.checkout ?? null,
      checkoutAmount: u?.checkoutAmount ?? '',
      accessEndsAt: u?.accessEndsAt ?? null,
      accessCode: u?.accessCode ?? '',
      consumerPremium: u?.consumerPremium ?? false,
      lastSignInAt: signIns.get(m.uid) ?? null,
    })
  }

  for (const c of companies) {
    // Admins first, then by name — the person to talk to should be at the top.
    c.members.sort(
      (a, b) =>
        Number(b.role === 'admin') - Number(a.role === 'admin') ||
        (a.name || a.email).localeCompare(b.name || b.email),
    )
  }
  companies.sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
  return companies
}

/**
 * Route and tour counts for the companies themselves.
 *
 * One pass over each collection rather than an aggregation per company: 546
 * routes and 61 tours is less data than 86 count queries would cost in round
 * trips, and reading them whole is what makes the unattributed share visible —
 * an aggregation can count what matches, not what is missing a field.
 */
export async function enrichProCompanies(companies: ProCompany[]): Promise<ProCompany[]> {
  const tally = async (name: string, guideKey: 'composite' | 'bare') => {
    const total = new Map<string, number>()
    const orphan = new Map<string, number>()
    const latest = new Map<string, string>()
    const snap = await getDocs(collection(productDb, name))
    for (const d of snap.docs) {
      const co = str(d.get('companyId'))
      if (!co) continue
      total.set(co, (total.get(co) ?? 0) + 1)
      if (!str(d.get('guideId'))) orphan.set(co, (orphan.get(co) ?? 0) + 1)
      const at = toIsoOrNull(d.get('updatedAt')) ?? toIsoOrNull(d.get('createdAt'))
      if (at && at > (latest.get(co) ?? '')) latest.set(co, at)
    }
    void guideKey
    return { total, orphan, latest }
  }
  // Deliberately not caught here any more.
  //
  // It used to swallow the failure and return the companies untouched, which
  // rendered every one of the 86 rows as "— routes · — tours" — indistinguishable
  // from a company that has built nothing. The data is there (546 routes across
  // 31 of these companies), so a dash on every row means the *read* failed, and
  // decision 3 says a figure the system cannot produce states its reason rather
  // than showing a blank. The caller now has something to say.
  {
    const [routes, tours] = await Promise.all([
      tally(PRODUCT_COLLECTIONS.proRoutes, 'composite'),
      tally(PRODUCT_COLLECTIONS.proTours, 'bare'),
    ])
    return companies.map((c) => ({
      ...c,
      routes: routes.total.get(c.id) ?? 0,
      tours: tours.total.get(c.id) ?? 0,
      unattributedRoutes: routes.orphan.get(c.id) ?? 0,
      unattributedTours: tours.orphan.get(c.id) ?? 0,
      lastWorkAt: [routes.latest.get(c.id), tours.latest.get(c.id)].filter(Boolean).sort().pop() ?? null,
    }))
  }
}

/**
 * Route and tour counts, one aggregation query per guide.
 *
 * Separate from the main load and called after it, because a hundred round
 * trips should not hold up the names — the same reason the consumer table
 * enriches in a second pass. Returns the rows patched, or unchanged if the
 * collections are not readable.
 */
export async function enrichProMembers(members: ProMember[]): Promise<ProMember[]> {
  const uids = [...new Set(members.map((m) => m.uid))]
  const [routes, tours] = await Promise.all([countRoutes(members), countTours(uids)])
  return members.map((m) => ({
    ...m,
    routes: routes?.get(`${m.companyId}_${m.uid}`) ?? null,
    tours: tours?.get(m.uid) ?? null,
  }))
}

/** Keyed by `companyId_uid`, because a guide can be in more than one company. */
async function countRoutes(members: ProMember[]): Promise<Map<string, number> | null> {
  try {
    const out = new Map<string, number>()
    await Promise.all(
      members.map(async (m) => {
        const q = query(
          collection(productDb, PRODUCT_COLLECTIONS.proRoutes),
          where('guideId', '==', `${m.companyId}_${m.uid}`),
        )
        out.set(`${m.companyId}_${m.uid}`, (await getCountFromServer(q)).data().count)
      }),
    )
    return out
  } catch {
    return null
  }
}

/** Tours key on the bare uid, so one query per person rather than per membership. */
async function countTours(uids: string[]): Promise<Map<string, number> | null> {
  try {
    const out = new Map<string, number>()
    await Promise.all(
      uids.map(async (uid) => {
        const q = query(collection(productDb, PRODUCT_COLLECTIONS.proTours), where('guideId', '==', uid))
        out.set(uid, (await getCountFromServer(q)).data().count)
      }),
    )
    return out
  } catch {
    return null
  }
}

/**
 * The guide-profile fields Skida PRO asks for during onboarding. Filling these
 * is what makes a guide presentable to a client, so "3 of 6" says more than a
 * completion flag on its own.
 */
const PROFILE_FIELDS = ['photo', 'about', 'educations', 'phone', 'language', 'country'] as const

interface UserFacts {
  name: string
  email: string
  createdAt: string | null
  access: ProMember['access']
  checkout: ProMember['checkout']
  checkoutAmount: string
  accessEndsAt: string | null
  accessCode: string
  consumerPremium: boolean
  onboardingCompleted: boolean | null
  onboardingStep: string
  onboardedAt: string | null
  onboardingUpdatedAt: string | null
  profileFilled: number
}

function filled(v: unknown): boolean {
  if (v === null || v === undefined) return false
  if (typeof v === 'string') return v.trim().length > 0
  if (Array.isArray(v)) return v.length > 0
  if (typeof v === 'object') return Object.keys(v as object).length > 0
  return true
}

async function loadUsers(uids: string[]) {
  const out = new Map<string, UserFacts>()
  try {
    for (let i = 0; i < uids.length; i += 30) {
      const snap = await getDocs(
        query(collection(productDb, PRODUCT_COLLECTIONS.users), where('uid', 'in', uids.slice(i, i + 30))),
      )
      for (const d of snap.docs) {
        const v = d.data()
        const ob = (v.onboarding ?? null) as DocumentData | null
        // PRO access, in the order that decides it: a paid agreement outranks a
        // grant, and a grant is only access while it is live.
        const sub = v.subscription as DocumentData | undefined
        const grant = (v.skidaProAccess ?? v.entitlement ?? v.promoAccess) as
          | DocumentData
          | undefined
        const expiresAt = str(grant?.expiresAt)
        const today = new Date().toISOString().slice(0, 10)
        const grantLive =
          grant?.active === true && (!expiresAt || expiresAt > new Date().toISOString())

        // ePay writes `subscription` when the payment window opens, so its
        // presence proves an attempt and nothing more. Only SUCCESS inside its
        // period is access; everything else falls through to the grant check,
        // because an abandoned checkout does not cancel a live promo grant.
        const state = str(sub?.state).toUpperCase()
        const periodEnd = str(sub?.periodeEnd)
        const paidLive = state === 'SUCCESS' && (!periodEnd || periodEnd >= today)

        const checkout: ProMember['checkout'] =
          state === 'SUCCESS'
            ? paidLive
              ? null
              : 'lapsed'
            : state === 'CANCELLED'
              ? 'cancelled'
              : state
                ? 'pending'
                : null

        const access: ProMember['access'] = paidLive
          ? 'paid'
          : grant
            ? grantLive
              ? 'granted'
              : 'expired'
            : 'registered'
        const premium = (v.activeEntitlements as DocumentData | undefined)?.Premium

        out.set(d.id, {
          access,
          checkout,
          checkoutAmount:
            typeof sub?.billingAmount === 'number'
              ? `${Math.round(sub.billingAmount / 100)} ${str(sub?.billingCurrency) || ''}`.trim()
              : '',
          accessEndsAt:
            access === 'paid'
              ? periodEnd || null
              : access === 'granted' || access === 'expired'
                ? expiresAt || null
                : null,
          accessCode: str(grant?.code),
          consumerPremium: Boolean(premium),
          // displayName is what the guide chose to be called in PRO; `name`
          // comes from the sign-in provider. Prefer the deliberate one.
          name: str(v.displayName) || str(v.name),
          email: str(v.email),
          createdAt: toIsoOrNull(v.createdAt),
          onboardingCompleted: ob ? ob.completed === true : null,
          onboardingStep: ob ? str(ob.currentStep) : '',
          onboardedAt: ob ? toIsoOrNull(ob.completedAt) : null,
          onboardingUpdatedAt: ob ? toIsoOrNull(ob.updatedAt) : null,
          profileFilled: PROFILE_FIELDS.filter((f) => filled(v[f])).length,
        })
      }
    }
  } catch {
    /* leaves names blank rather than failing the whole view */
  }
  return out
}


async function loadSignIns(uids: string[]) {
  const out = new Map<string, string>()
  try {
    for (let i = 0; i < uids.length; i += 30) {
      const snap = await getDocs(
        query(collection(db, CRM_COLLECTIONS.proMembers), where('uid', 'in', uids.slice(i, i + 30))),
      )
      for (const d of snap.docs) {
        const iso = toIsoOrNull(d.get('lastSignInAt'))
        if (iso) out.set(str(d.get('uid')), iso)
      }
    }
  } catch {
    /* the sync has not run, or the collection is not readable */
  }
  return out
}
